import {get, patch, post} from 'utils/axios'
import {AxiosError} from 'axios'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'
import {
 CommonDropdownOption,
 CreateTicketPayload,
 CreateTicketResponse,
 FetchAllTicketsResponse,
 GetTicketDetailResponse,
 TicketStatus,
 TutorialsApiResponse,
 TutorialsResponse,
 UpdateTicketPayload,
 UpdateTicketResponse,
 UpsertFeedbackPayload,
 UpsertFeedbackResponse,
} from 'models/Ticket'
import {toast} from 'react-toastify'
import {IFormData, IFormField} from 'models/Common'

export type ModuleDTO = {
 id: number
 name: string
}

export type IssueTypeDTO = {
 id: number
 name: string
}

export type FetchAllTicketsParams = {
 ticket_status?: TicketStatus[]
 start_date?: string
 end_date?: string
 page?: number
 page_size?: number
 search_by?: string
 priority?: string
 state_id?: number
}

const createTicketApi = async (
 payload: CreateTicketPayload
): Promise<CreateTicketResponse> => {
 try {
  const res = await post<CreateTicketResponse>(
   `/ticket/v1/create?app_code=web&lang=en`,
   payload
  )

  return res
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {
   success: false,
   message: 'Something went wrong',
   data: {
    ticket_number: '',
    message: '',
   },
  }
 }
}

const getModulesApi = async (): Promise<ModuleDTO[]> => {
 try {
  const res = await get<{
   success: boolean
   data: ModuleDTO[]
  }>(`/common/v1/modules?app_code=web&lang=en`)

  if (res?.success) {
   return res.data
  }

  return []
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return []
 }
}

const getIssueTypesApi = async (
 moduleId: number
): Promise<IssueTypeDTO[]> => {
 try {
  const res = await get<{
   success: boolean
   data: IssueTypeDTO[]
  }>(`/common/v1/issue-types?module_id=${moduleId}`)

  if (res?.success) {
   return res.data
  }

  return []
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return []
 }
}

const fetchAllTicketsApi = async (
 params: FetchAllTicketsParams
): Promise<FetchAllTicketsResponse> => {
 try {
  const {
   ticket_status,
   start_date,
   end_date,
   page = 1,
   page_size = 10,
   search_by = '',
   priority,
   state_id,
  } = params

  const queryParams: IFormField = {
   start_date,
   end_date,
   page,
   page_size,
   search_by,
   priority,
   state_id,
  }

  if (ticket_status && ticket_status.length > 0) {
   queryParams.ticket_status = ticket_status
  }

  const res = await get<FetchAllTicketsResponse>(
   '/ticket/v1/list',
   queryParams
  )

  return res
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {
   count: 0,
   next: null,
   previous: null,
   results: [],
  }
 }
}

const getTicketDetailApi = async (
 ticketNumber: string
): Promise<GetTicketDetailResponse> => {
 try {
  const res = await get<GetTicketDetailResponse>(
   `/ticket/v1/detail/${ticketNumber}`
  )

  return res
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {
   success: false,
   message: 'Something went wrong',
   data: {
    ticket_number: '',
    ticket_date: '',
    ticket_raised_by: '',
    user_type: '',
    phone_number: '',
    module: '',
    issue_type: '',
    issue_description: '',
    ticket_status: 'OPEN',
    updated_on: '',
    user_feedback_comment: '',
    feedback_status: null,
    attachments: [],
   },
  }
 }
}

const updateTicketApi = async (
 ticketNumber: string,
 payload: UpdateTicketPayload
): Promise<UpdateTicketResponse> => {
 try {
  const res = await patch<UpdateTicketResponse>(
   `/ticket/v1/update/${ticketNumber}`,
   payload
  )
  if (res?.success) {
   toast.success(
    res.data.message || 'Ticket updated successfully'
   )
  }
  return res
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {
   success: false,
   message: 'Something went wrong',
   data: {
    ticket_number: ticketNumber,
    status: payload.status,
    message: '',
   },
  }
 }
}

const upsertTicketFeedbackApi = async (
 payload: UpsertFeedbackPayload
): Promise<UpsertFeedbackResponse> => {
 try {
  const res = await post<UpsertFeedbackResponse>(
   `/ticket/v1/feedback`,
   payload
  )

  if (res?.success) {
   toast.success(
    res.message || 'Feedback submitted successfully'
   )
  }

  return res
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {
   success: false,
   message: 'Something went wrong',
   data: {
    feedback_id: 0,
    ticket_number: payload.ticket_number,
    feedback_status: payload.feedback_status,
    message: '',
   },
  }
 }
}

const getCommonDropdownApi = async (
 dropdownType: string
): Promise<CommonDropdownOption[]> => {
 try {
  const res = await get<{
   success: boolean
   message: string
   data: CommonDropdownOption[]
  }>(`/common/v1/dropdown?dropdown_type=${dropdownType}`)

  if (res?.success) {
   return res.data
  }

  return []
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return []
 }
}
const getTutorialsApi = async (
 url?: string,
 filters?: IFormData
): Promise<TutorialsApiResponse> => {
 try {
  const res = await get<TutorialsResponse>(
   url ?? `/common/v1/tutorials`,
   {
    app_code: 'web',
    ...(filters ?? {}),
   }
  )

  return {
   results: res?.results ?? [],
   next: res?.next ?? null,
  }
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return {results: [], next: null}
 }
}

const fetchUserCategoryDropdown = async () => {
 try {
  const res = await get<{
   success: boolean
   message: string
   data: {label: string; value: string}[]
  }>(`/common/v1/dropdown`, {
   dropdown_type: 'user_category',
  })

  return res?.data ?? []
 } catch (err) {
  console.error(err)
  return []
 }
}

export {
 createTicketApi,
 getModulesApi,
 getIssueTypesApi,
 fetchAllTicketsApi,
 getTicketDetailApi,
 updateTicketApi,
 upsertTicketFeedbackApi,
 getCommonDropdownApi,
 getTutorialsApi,
 fetchUserCategoryDropdown,
}
